import { Pause, Play, Square } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/v2/components/ui/tooltip";
import { useTimerContext } from "@/v2/contexts/TimerProvider";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import { formatDurationClock } from "@/v2/lib/time";
import { cn } from "@/v2/lib/utils";

export function TimerPill() {
	const timer = useTimerContext();
	const isMobile = useIsMobile();
	const navigate = useNavigate();
	const location = useLocation();

	if (timer.phase === "idle") return null;

	const displaySeconds =
		timer.phase === "stopped" ? timer.finalSeconds : timer.elapsedSeconds;
	const isRunning = timer.phase === "running";
	const isPaused = timer.phase === "paused";
	const isStopped = timer.phase === "stopped";

	const handleFinish = () => {
		timer.finishAndRequestAddEntry();
		if (location.pathname !== "/entries") navigate("/entries");
	};

	return (
		<div
			className={cn(
				"fixed z-40 flex items-center gap-1.5 rounded-2xl border border-border bg-background/95 shadow-lg backdrop-blur-sm",
				"animate-in fade-in zoom-in-95 duration-200",
				isMobile
					? "bottom-[calc(4rem+env(safe-area-inset-bottom)+12px)] left-4"
					: "top-4 right-[calc(1rem+var(--sidebar-width,0px))]",
			)}
		>
			<div className="flex items-center gap-1.5 py-2.5 pl-3.5">
				<div
					className={cn(
						"size-2.5 rounded-full",
						isRunning && "bg-success animate-pulse",
						(isPaused || isStopped) && "bg-warning",
					)}
				/>
				<span className="tabular-nums text-base font-semibold pl-1">
					{formatDurationClock(displaySeconds)}
				</span>
			</div>

			{/* Action buttons */}
			<div className="flex items-center pr-1.5">
				{isRunning && (
					<>
						<PillButton onClick={timer.pause} label="Pause">
							<Pause className="size-4" />
						</PillButton>
						<PillButton onClick={handleFinish} label="Finish and add entry">
							<Square className="size-3.5 fill-current" />
						</PillButton>
					</>
				)}

				{isPaused && (
					<>
						<PillButton onClick={timer.resume} label="Resume">
							<Play className="size-4 fill-current" />
						</PillButton>
						<PillButton onClick={handleFinish} label="Finish and add entry">
							<Square className="size-3.5 fill-current" />
						</PillButton>
					</>
				)}

				{isStopped && (
					<>
						<PillButton onClick={timer.resume} label="Resume">
							<Play className="size-4 fill-current" />
						</PillButton>
						<PillButton onClick={handleFinish} label="Add entry">
							<Square className="size-3.5 fill-current" />
						</PillButton>
					</>
				)}
			</div>
		</div>
	);
}

function PillButton({
	onClick,
	label,
	children,
}: {
	onClick: () => void;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						type="button"
						onClick={onClick}
						aria-label={label}
						className="inline-flex items-center justify-center size-9 rounded-xl text-muted-foreground transition-colors hover:text-foreground hover:bg-accent active:bg-accent/80"
					>
						{children}
					</button>
				</TooltipTrigger>
				<TooltipContent side="bottom">{label}</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
